<template>
  <PageContent type="center" :compClass="'flex flex-col space-y-6 pt-6'">
    <div class="flex flex-row items-center">
      <div class="flex-1">
        <h1 class="text-xl font-semibold">Dashboard</h1>
      </div>
      <div class="flex flex-row justify-end space-x-2">
        <router-link to="/admin/tools/email-generator"
          class="h-8 flex items-center justify-center text-sm px-4 rounded-full text-white bg-blue-600 hover:bg-blue-500 transition-all">
          Email Generator
        </router-link>
        <router-link :to="{ name: 'MediaLatestContent' }"
          class="h-8 flex items-center justify-center text-sm px-4 rounded-full text-white bg-blue-600 hover:bg-blue-500 transition-all">
          Media
        </router-link>
        <router-link :to="{ name: 'StringerUpload' }"
          class="h-8 flex items-center justify-center text-sm px-4 rounded-full text-white bg-blue-600 hover:bg-blue-500 transition-all">
          Upload
        </router-link>
        <router-link :to="{ name: 'StringerHome' }"
          class="h-8 flex items-center justify-center text-sm px-4 rounded-full text-white bg-blue-600 hover:bg-blue-500 transition-all">
          Stringer
        </router-link>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <StatCard title="Recent Sales" descriptor="Past Week" innerHeight="h-48">
        <VueApexCharts type="area" height="100%" ref="salesChart" :options="chartOptions" :series="series" />
      </StatCard>
      <StatCard title="Recent Email" :descriptor="
        (recentEmails.length > 0 ? recentEmails[0].created_at : null)
        | displayLocalDateTime
      " innerHeight="md:h-48 group relative">
        <div
          class="z-20 absolute w-full h-full top-0 left-0 flex items-center justify-center opacity-0 transition-all group-hover:opacity-100">
          <router-link
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            :to="{ name: 'AdminToolsMailList' }">
            Go to Recent Emails
          </router-link>
        </div>
        <div class="z-10 divide-y divide-gray-200 -mt-3 mb-3 filter group-hover:blur-sm transition-all select-none"
          v-if="recentEmails.length > 0">
          <div class="flex flex-row items-center py-3 group px-4">
            <div class="flex-1">
              Sent
            </div>
            <span>{{ recentEmails[0].analytics.sentEmails }}</span>
          </div>
          <div class="flex flex-row items-center py-3 group px-4">
            <div class="flex-1">
              Views
            </div>
            <span>{{ recentEmails[0].analytics.openedEmails }}</span>
          </div>
          <div class="flex flex-row items-center py-3 group px-4">
            <div class="flex-1">
              Previews
            </div>
            <span>{{ recentEmails[0].analytics.linksFollowed }}</span>
          </div>
          <div class="flex flex-row items-center py-3 group px-4">
            <div class="flex-1">
              Purchases
            </div>
            <span>{{ recentEmails[0].analytics.purchases }}</span>
          </div>
        </div>
      </StatCard>
      <StatCard title="Users Overview" :descriptor="
        totalOnlineUsers ? totalOnlineUsers + ' online users' : null
      " innerHeight="lg:h-48 flex lg:flex-row flex-col items-center pb-4 lg:pb-0">
        <div class="h-40 flex items-center">
          <VueApexCharts height="100%" type="donut" ref="usersChart" :options="pieChartOptions" :series="pieSeries" />
        </div>
        <div class="flex flex-col">
          <div class="flex flex-row items-center space-x-2 py-0.5" v-for="(item, key) in pieChartOptions.labels"
            v-bind:key="key">
            <div class="w-4 h-2.5 rounded-full" :style="{ 'background-color': pieChartOptions.colors[key] }"></div>
            <div class="text-sm">{{ item }}</div>
          </div>
        </div>
      </StatCard>
    </div>

    <div class="w-full flex flex-col" v-if="upcomingVideos.length > 0">
      <h1 class="font-medium mb-2 tracking-wide text-gray-600">
        Upcoming videos
      </h1>
      <div class="shadow ring-1 ring-black ring-opacity-5">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50 rounded-t-lg overflow-hidden">
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                Name
              </th>
              <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                User
              </th>
              <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                Uploaded At
              </th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Status
              </th>
              <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                <span class="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white md:rounded-b-lg">
            <tr v-for="video in upcomingVideos" v-bind:key="video.id">
              <td class="w-full max-w-0 py-4 pl-4 pr-3 text-sm text-gray-700 sm:w-auto sm:max-w-none sm:pl-6">
                {{ video.title }}
                <dl class="font-normal lg:hidden">
                  <dd class="mt-1 truncate text-gray-700">
                    {{ video.user.name }}
                  </dd>
                  <dd class="mt-1 truncate text-gray-700 sm:hidden">
                    {{ video.created_at | displayLocalDateTime }}
                  </dd>
                </dl>
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-700 lg:table-cell">
                {{ video.user.name }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-700 sm:table-cell">
                {{ video.created_at | displayLocalDateTime }}
              </td>
              <td class="px-3 py-4 text-sm text-gray-700">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 uppercase"
                  v-if="video.status == 'uploading'">
                  User Uploading
                </span>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 uppercase"
                  v-if="video.status == 'trf_aws'">
                  Transferring to AWS
                </span>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 uppercase"
                  v-if="video.status == 'aws_trans'">
                  AWS Transcoding
                </span>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 uppercase"
                  v-if="video.status == 'yt_process'">
                  YouTube Processing
                </span>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 uppercase"
                  v-if="video.status == 'pending'">
                  Pending
                </span>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 uppercase"
                  v-if="video.status == 'error'">
                  Error
                </span>
              </td>
              <td class="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-2">
                <Dropdown buttonClass="focus:outline-none">
                  <template #trigger>
                    <span class="flex items-center h-5 w-5 text-lg">
                      <i class="far fa-ellipsis-h"></i>
                    </span>
                  </template>
                  <template #dropdown>
                    <div class="py-1" role="none">
                      <button
                        class="text-left w-full block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem" v-on:click="deleteVideo(video)">
                        Delete Video
                      </button>
                      <button
                        class="text-left w-full block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem" v-on:click="openApprovalModal(video)">
                        Review Video
                      </button>
                      <button
                        class="text-left w-full block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        role="menuitem" @click="reprocessYouTube(video.id)" v-if="
                          video.status == 'pending' && video.youtube_id == null
                        ">
                        Reupload YouTube
                      </button>
                    </div>
                  </template>
                </Dropdown>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="">
      <h1 class="font-medium mb-2 tracking-wide text-gray-600">
        Active Users
      </h1>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <div class="bg-white rounded-lg px-4 py-2 flex flex-row text-sm items-center space-x-4 flex-wrap"
          v-for="user in activeUsers" v-bind:key="user.id">
          <div :class="{
            'text-green-400': !determineIsIdle(user.last_active),
            'text-yellow-400': determineIsIdle(user.last_active)
          }" :title="
  !determineIsIdle(user.last_active)
    ? 'User is currently active'
    : 'User is currently idle'
" v-tippy>
            <i class="fa-solid fa-circle-small"></i>
          </div>
          <div class="flex-1 flex flex-col">
            <span><router-link class=" text-gray-900" :to="{
              name: 'AdminUserInformation',
              params: { uid: user.user_id }
            }">
                {{ user.name }}
              </router-link>
              <!-- <i
                  class="fa-solid fa-desktop text-gray-400"
                  v-if="user.method == 'Web'"
                ></i> -->
            </span>
            <div class="text-gray-500">
              <span v-if="!user.role.includes('media')">{{
                user.role | displayRole
              }}</span>

              <span v-if="user.role.includes('media') && user.network !== null">
                {{ user.network.name }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <h1 class="font-medium mb-2 tracking-wide text-gray-600">
        Recent Sales
      </h1>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" v-if="recentSales.length > 0">
        <div class="bg-white rounded-lg px-4 py-2 flex flex-col text-sm" v-for="sale in recentSales"
          v-bind:key="sale.id">
          <span class="whitespace-nowrap overflow-ellipsis overflow-hidden" :title="sale.video.title" v-tippy>{{
            sale.video.title
          }}</span>
          <span class="h-2"></span>
          <span><i class="fas fa-user pr-1 text-gray-400" style="line-height: initial"></i>
            {{ sale.owner.name }}
            <i class="fa-regular fa-circle-exclamation ml-1 text-red-600" v-if="sale.owner.w9_timestamp == null" v-tippy
              title="User has not submitted a W-9 form yet."></i></span>
          <span>
            <i class="far fa-building pr-1 text-gray-400" style="line-height: initial"></i>
            {{ sale.network.name }}
          </span>
        </div>
      </div>
      <span class="text-gray-600 text-sm" v-if="recentSales.length == 0">There are currently no recent sales.</span>
    </div>

    <VideoApprovalModal />
  </PageContent>
</template>

<script>
import * as Config from "@/config";
import moment from "moment";
import PageContent from "@/components/PageContent";
import StatCard from "@/components/StatCard";
import VueApexCharts from "vue-apexcharts";
import { displayRole, getRoleColor } from "@/filters";
import { determineIsIdle } from "@/helpers";

import Dropdown from "@/components/partials/Dropdown";
import VideoApprovalModal from "@/screens/admin/modals/VideoApproveModal";

export default {
  name: "AdminHome",
  data() {
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      polling: true,

      series: [
        {
          name: "Sales",
          data: []
        }
      ],
      chartOptions: {
        chart: {
          type: "area",
          toolbar: {
            show: false
          },
          selection: {
            enabled: false
          },
          sparkline: {
            enabled: true
          },
          zoom: {
            enabled: false
          }
        },
        grid: {
          show: false,
          padding: {
            top: 4,
            left: 0,
            right: 0,
            bottom: 0
          }
        },
        stroke: {
          curve: "smooth"
        },
        tooltip: {
          x: {
            show: true
          },
          y: {
            formatter: function (value) {
              return Math.round(value);
            }
          }
        }
      },
      pieSeries: [0],
      pieChartOptions: {
        chart: {
          type: "donut"
        },
        dataLabels: {
          enabled: false
        },
        labels: [],
        colors: ["#fff"],
        legend: {
          show: false
        }
      },
      upcomingVideos: [],
      recentEmails: [],
      totalOnlineUsers: null,
      activeUsers: [],
      recentSales: [],
      refreshInterval: null
    };
  },
  components: {
    VueApexCharts,
    PageContent,
    StatCard,
    VideoApprovalModal,
    Dropdown
  },
  mounted() {
    this.getSalesGraph();
    this.getRecentSales();
    this.getUpcomingVideos();
    this.getLastThreeEmails();
    this.getActiveUsers();

    this.$nextTick(
      function () {
        this.$eventBus.$on(
          "admin-refresh-upcoming-videos",
          this.getUpcomingVideos
        );
      }.bind(this)
    );

    this.refreshInterval = setInterval(
      function () {
        this.getSalesGraph();
        this.getRecentSales();
        this.getUpcomingVideos();
        this.getLastThreeEmails();
        this.getActiveUsers();
      }.bind(this),
      1000 * 60
    );
  },
  destroyed() {
    this.$eventBus.$off(
      "admin-refresh-upcoming-videos",
      this.getUpcomingVideos
    );
    if (this.refreshInterval !== null) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  },
  methods: {
    determineIsIdle: determineIsIdle,
    async getSalesGraph() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/graphs/sales",
        {
          params: {
            filter: "week",
            timezone: this.timezone
          }
        }
      );

      let newData = [];
      for (const [key, value] of Object.entries(res.data)) {
        newData.push({
          x: moment(key, "YYYY-MM-DD").format("MM-DD-YYYY"),
          y: value
        });
      }
      this.series[0].data = newData;
      this.createSalesGraph();
    },
    async getUpcomingVideos() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/videos/upcoming?v2"
      );
      this.$set(this, "upcomingVideos", res.data);
    },
    createSalesGraph() {
      this.$refs.salesChart.updateSeries(
        [
          {
            data: this.series[0].data
          }
        ],
        false,
        true
      );
    },

    async getLastThreeEmails() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/mail/dashboard/recent"
      );
      this.$set(this, "recentEmails", res.data);
    },
    async getActiveUsers() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/users/active?v2"
      );
      let analytics = res.data.analytics;
      // console.log(Object.keys(analytics));
      let series = [];
      let labels = [];
      let colors = [];
      let total = 0;
      Object.keys(analytics).forEach(key => {
        series.push(analytics[key]);
        labels.push(displayRole(key));
        colors.push(getRoleColor(key));
        total += analytics[key];
      });
      this.$set(this, "pieSeries", series);
      this.pieChartOptions = {
        labels: labels,
        colors: colors
      };
      this.totalOnlineUsers = total;
      this.activeUsers = res.data.users;
    },
    async getRecentSales() {
      let res = await this.$http.get(
        Config.apiPath + "/api/v2/admin/sales/overview"
      );
      this.recentSales = res.data;
    },
    openApprovalModal(video) {
      this.$eventBus.$emit("admin-approve-modal-open", video);
    },
    async reprocessYouTube(id) {
      await this.$http.post(
        Config.apiPath + "/api/v2/admin/videos/" + id + "/youtube_upload"
      );
      alert("Processing...");
    },
    async deleteVideo(video) {
      let result = await this.$swal({
        title: "Continue?",
        text: "Are you sure you want to remove this upload?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, remove upload."
      });

      if (result.isConfirmed) {
        var data = {
          id: video.id
        };
        let res = await this.$http.post(
          Config.apiPath + "/api/v2/admin/videos/remove",
          data
        );
        if (res.status == 200) {
          this.getUpcomingVideos();
        }
      }
    }
  }
};
</script>
